import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, ArrowRight, ExternalLink } from 'lucide-react';
import { api } from '../../utils/api';

const WhatsAppMiniIcon = () => (
  <svg viewBox="0 0 32 32" fill="currentColor" className="h-4 w-4">
    <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.128-1.958A15.89 15.89 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.35 22.608c-.39 1.1-1.932 2.014-3.168 2.28-.846.18-1.95.324-5.668-1.218-4.762-1.972-7.826-6.814-8.064-7.13-.23-.316-1.932-2.574-1.932-4.91 0-2.336 1.222-3.482 1.656-3.96.39-.432.92-.626 1.23-.626.152 0 .288.008.41.014.434.02.652.046.938.726.358.852 1.228 3.006 1.334 3.226.108.22.216.518.068.818-.14.306-.264.496-.484.762-.22.266-.432.468-.652.754-.198.252-.422.522-.176.962.246.434 1.094 1.804 2.35 2.922 1.614 1.436 2.974 1.882 3.396 2.09.434.216.686.182.938-.108.258-.296 1.104-1.286 1.398-1.728.288-.434.582-.362.978-.216.4.144 2.538 1.198 2.972 1.416.434.22.724.324.832.504.106.182.106 1.048-.284 2.148z"/>
  </svg>
);

const ChatMessageContent = ({ content }) => {
  // Step 1: Replace markdown links [text](url) with placeholders
  const placeholders = [];
  let processed = content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
    const idx = placeholders.length;
    placeholders.push({ type: 'link', text, url });
    return `%%LINK_${idx}%%`;
  });

  // Step 2: Replace raw WhatsApp URLs that weren't already part of a markdown link
  processed = processed.replace(/(https?:\/\/wa\.me\/[^\s)]+)/g, (match) => {
    const idx = placeholders.length;
    placeholders.push({ type: 'link', text: 'Chat on WhatsApp', url: match });
    return `%%LINK_${idx}%%`;
  });

  // Step 3: Split by placeholders and build React elements
  const segments = processed.split(/(%%LINK_\d+%%)/);

  return (
    <div className="space-y-2">
      {segments.map((segment, i) => {
        const linkMatch = segment.match(/^%%LINK_(\d+)%%$/);
        if (linkMatch) {
          const link = placeholders[parseInt(linkMatch[1])];
          const isWhatsApp = link.url.includes('wa.me') || link.text.toLowerCase().includes('whatsapp');
          return (
            <a
              key={i}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`mt-2 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold transition-all hover:scale-[1.02] no-underline ${
                isWhatsApp
                  ? 'bg-[#25D366] text-white hover:bg-[#1EBE57] shadow-sm'
                  : 'bg-[#F3EBE1] text-[#3E2723] hover:bg-[#E6DCD3]'
              }`}
              data-testid="chat-whatsapp-cta"
            >
              {isWhatsApp ? <WhatsAppMiniIcon /> : <ExternalLink className="h-3 w-3" />}
              {link.text}
            </a>
          );
        }
        if (!segment) return null;
        return (
          <span
            key={i}
            className="whitespace-pre-wrap"
            dangerouslySetInnerHTML={{
              __html: segment
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\n/g, '<br/>')
            }}
          />
        );
      })}
    </div>
  );
};

const QUICK_ACTIONS = [
  { label: 'View Services', message: 'What hairstyles and services do you offer?' },
  { label: 'Help Me Book', message: 'How do I book an appointment?' },
  { label: 'Payment Help', message: 'How does payment work?' },
  { label: 'Policies', message: 'What are your booking and cancellation policies?' },
  { label: 'Contact Admin', message: 'I need to speak with someone directly.' },
];

const getSessionId = () => {
  let id = sessionStorage.getItem('cbp_chat_session');
  if (!id) {
    id = 'chat_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    sessionStorage.setItem('cbp_chat_session', id);
  }
  return id;
};

const ChatWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(getSessionId());

  // Check if on admin page
  const isAdminPage = location.pathname.startsWith('/admin');

  const pageContext = useMemo(() => {
    const path = location.pathname;
    const search = location.search;
    const map = {
      '/': 'Homepage',
      '/services': 'Services page',
      '/gallery': 'Gallery page',
      '/booking': 'Booking page',
      '/payment': 'Payment page',
      '/contact': 'Contact page',
      '/faq': 'FAQ page',
      '/policies': 'Policies page',
      '/about': 'About page',
      '/dashboard': 'Customer dashboard',
    };
    let ctx = map[path] || `Page: ${path}`;
    if (search) ctx += ` ${search}`;
    return ctx;
  }, [location.pathname, location.search]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load history when first opened
  useEffect(() => {
    if (isOpen && !historyLoaded && !isAdminPage) {
      api.getChatHistory(sessionId.current)
        .then(res => {
          if (res.data && res.data.length > 0) {
            setMessages(res.data.map(m => ({ role: m.role, content: m.content })));
          }
        })
        .catch(() => {})
        .finally(() => setHistoryLoaded(true));
    }
  }, [isOpen, historyLoaded, isAdminPage]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.sendChatMessage({
        message: text.trim(),
        session_id: sessionId.current,
        page_context: pageContext,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having a little trouble connecting right now. Please try again in a moment."
      }]);
    } finally {
      setLoading(false);
    }
  }, [loading, pageContext]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    sendMessage(input);
  }, [input, sendMessage]);

  const handleQuickAction = useCallback((action) => {
    sendMessage(action.message);
  }, [sendMessage]);

  // Hide on admin pages - return null AFTER all hooks
  if (isAdminPage) {
    return null;
  }

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 right-6 z-50 h-14 w-14 rounded-full bg-[#3E2723] text-white shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-[#2A1A17] hover:scale-105 hover:shadow-xl ${isOpen ? 'scale-0 opacity-0 pointer-events-none' : 'scale-100 opacity-100'}`}
        data-testid="chat-fab-button"
        aria-label="Open chat assistant"
      >
        <MessageCircle className="h-6 w-6" />
      </button>

      {/* Chat Panel */}
      <div
        className={`fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 transition-all duration-300 origin-bottom-right ${
          isOpen
            ? 'scale-100 opacity-100 pointer-events-auto'
            : 'scale-0 opacity-0 pointer-events-none'
        }`}
        data-testid="chat-panel"
      >
        <div className="w-screen h-screen sm:w-[400px] sm:h-[560px] sm:max-h-[80vh] bg-white sm:rounded-2xl shadow-2xl border border-[#E6DCD3] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-[#3E2723] px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-[#C9A961]/20 flex items-center justify-center">
                <MessageCircle className="h-4 w-4 text-[#C9A961]" />
              </div>
              <div>
                <h3 className="text-white font-medium text-sm">Crowns by Preshy</h3>
                <p className="text-[#C9A961] text-xs">Customer Assistant</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white transition-colors p-1"
              data-testid="chat-close-button"
              aria-label="Close chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-[#FCFBF9]" data-testid="chat-messages">
            {/* Welcome message if no history */}
            {messages.length === 0 && !loading && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-[#E6DCD3] max-w-[85%]">
                  <p className="text-sm text-[#1A1A1A] leading-relaxed">
                    Hi, welcome to Crowns by Preshy! I'm here to help you with services, booking, payments, or any questions you might have.
                  </p>
                </div>

                {/* Quick Actions */}
                <div className="space-y-2" data-testid="chat-quick-actions">
                  <p className="text-xs text-[#786E66] font-medium px-1">Quick questions:</p>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_ACTIONS.map((action, i) => (
                      <button
                        key={i}
                        onClick={() => handleQuickAction(action)}
                        className="text-xs bg-white border border-[#E6DCD3] text-[#3E2723] rounded-full px-3.5 py-2 hover:bg-[#F3EBE1] hover:border-[#C9A961] transition-all flex items-center gap-1.5"
                        data-testid={`quick-action-${i}`}
                      >
                        {action.label}
                        <ArrowRight className="h-3 w-3 opacity-50" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Chat Messages */}
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-message-${idx}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#3E2723] text-white rounded-br-sm'
                      : 'bg-white text-[#1A1A1A] rounded-tl-sm shadow-sm border border-[#E6DCD3]'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <ChatMessageContent content={msg.content} />
                  ) : (
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div className="flex justify-start" data-testid="chat-typing-indicator">
                <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-[#E6DCD3]">
                  <div className="flex gap-1.5 items-center h-5">
                    <span className="w-2 h-2 rounded-full bg-[#C9A961] animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-[#C9A961] animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-[#C9A961] animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form
            onSubmit={handleSubmit}
            className="flex-shrink-0 border-t border-[#E6DCD3] bg-white px-4 py-3 flex items-center gap-2"
            data-testid="chat-input-form"
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              disabled={loading}
              className="flex-1 text-sm bg-[#FCFBF9] border border-[#E6DCD3] rounded-full px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C9A961] focus:border-transparent placeholder:text-[#786E66] disabled:opacity-50"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="h-10 w-10 rounded-full bg-[#3E2723] text-white flex items-center justify-center hover:bg-[#2A1A17] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              data-testid="chat-send-button"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
